import { useCallback, useState } from 'react';
import { endOfBusinessDayIso } from '@/lib/time';

export interface GrantScopeState {
  expiresAt: string;
  reason: string;
  docs: Set<string>;
}

export interface GrantScopeFieldsApi {
  state: GrantScopeState;
  setExpiresAt: (v: string) => void;
  setReason: (v: string) => void;
  toggleDoc: (id: string) => void;
  reset: () => void;
  /** Vencimiento, motivo y al menos un documento. */
  complete: boolean;
  /** Payload común: vence al final de ese día en Costa Rica (la medianoche UTC ya
   *  habría pasado) y lectura sobre cada documento marcado. */
  payload: () => {
    expiresAt: string;
    reason: string;
    scope: { grants: { module: string; id: string; actions: string[] }[] };
  };
}

const EMPTY = (): GrantScopeState => ({ expiresAt: '', reason: '', docs: new Set() });

/** Estado de vencimiento + motivo + documentos de "Otorgar acceso" (una sola fuente
 *  para la regla del vencimiento y el alcance, en los tres caminos del buscador). */
export function useGrantScopeFields(): GrantScopeFieldsApi {
  const [state, setState] = useState<GrantScopeState>(EMPTY);
  const reset = useCallback(() => setState(EMPTY()), []);
  return {
    state,
    setExpiresAt: (expiresAt) => setState((s) => ({ ...s, expiresAt })),
    setReason: (reason) => setState((s) => ({ ...s, reason })),
    toggleDoc: (id) =>
      setState((s) => {
        const docs = new Set(s.docs);
        if (docs.has(id)) {
          docs.delete(id);
        } else {
          docs.add(id);
        }
        return { ...s, docs };
      }),
    reset,
    complete: state.expiresAt !== '' && state.reason.trim() !== '' && state.docs.size > 0,
    payload: () => ({
      expiresAt: endOfBusinessDayIso(state.expiresAt),
      reason: state.reason.trim(),
      scope: {
        grants: [...state.docs].map((id) => ({
          module: 'master_list_entries',
          id,
          actions: ['read'],
        })),
      },
    }),
  };
}
